import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { type RestaurantTagType } from '@discover-data/stores/restaurant/gql/fragments/restaurantTag';
import { type SuburbType } from '@discover-data/gql/fragments/suburb';
import { type PeopleOption } from '@discover-data/stores/hooks/people/options';
import { useModal } from '@context/ModalContext';
import useMapQueryState from './useMapQueryState';
import MapModal from './modal';

export default function useSyncMapState(
  defaultViewState: ViewState,
  tags: RestaurantTagType[],
  suburbs: SuburbType[],
  peopleOptions?: PeopleOption[] | null,
  minDiners?: PeopleOption | null,
  maxDiners?: PeopleOption | null
) {
  const { showModal, hideModal, isModalOpen } = useModal();
  const [mapOpen] = useMapQueryState();
  const router = useRouter();
  const [leavingPage, setLeavingPage] = useState(false);

  useEffect(() => {
    const handleStart = (_url: string, { shallow }: { shallow: boolean }) => {
      if (!shallow) {
        setLeavingPage(true);
        if (mapOpen && isModalOpen) hideModal();
      }
    };
    const handleStop = () => {
      setLeavingPage(false);
    };
    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleStop);
    router.events.on('routeChangeError', handleStop);
    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleStop);
      router.events.off('routeChangeError', handleStop);
    };
  }, [router.events, mapOpen, isModalOpen, hideModal]);

  useEffect(() => {
    // show the map
    if (mapOpen && !isModalOpen && !leavingPage) {
      showModal(
        <MapModal
          defaultViewState={defaultViewState}
          tags={tags}
          suburbs={suburbs}
          peopleOptions={peopleOptions}
          minDiners={minDiners}
          maxDiners={maxDiners}
        />,
        {
          showCloseIcon: false,
          disableEscExit: true,
          onRequestClose: hideModal,
          hideOnRouteChange: false,
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, mapOpen, leavingPage]);
}
